import React from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";

const SCANNER_ID = "signup-qr-scanner";

function isLikelyJwt(value: string): boolean {
    const parts = value.split(".");

    return parts.length === 3 && parts.every((part) => part.length > 0);
}

type Props = {
    theme: "light" | "dark";
    onRestore: (token: string) => void;
};

const QrRestoreStep = ({ theme, onRestore }: Props) => {
    const { t } = useTranslation();
    const [mode, setMode] = React.useState<"camera" | "file">("file");
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const scannerRef = React.useRef<Html5Qrcode | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const isDark = theme === "dark";

    const handleQrDetected = (data: string) => {
        if(!isLikelyJwt(data)) {
            setError(t("signup.qr_restore_invalid"));
            return;
        }

        stopCamera();
        setLoading(true);
        setError(null);

        onRestore(data);
    };

    const handleFileSelect = async (file: File) => {
        setError(null);

        try {
            const scanner = new Html5Qrcode(SCANNER_ID);
            const result = await scanner.scanFileV2(file, false);

            handleQrDetected(result.decodedText);
            await scanner.clear();
        } catch(_) {
            setError(t("signup.qr_restore_not_found"));
        }
    };

    const startCamera = async () => {
        setError(null);

        try {
            const scanner = new Html5Qrcode(SCANNER_ID);

            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 200, height: 200 } },
                (decodedText) => handleQrDetected(decodedText),
                () => undefined
            );
        } catch(_) {
            setError(t("signup.qr_restore_camera_error"));
            setMode("file");
        }
    };

    const stopCamera = async () => {
        if(!scannerRef.current) {
            return;
        }

        try {
            await scannerRef.current.stop();
            await scannerRef.current.clear();
        } catch(_) {
            // ignore
        }

        scannerRef.current = null;
    };

    React.useEffect(() => {
        let cancelled = false;

        const run = async () => {
            await stopCamera();
            await new Promise(r => setTimeout(r, 100));

            if(!cancelled && mode === "camera") {
                startCamera();
            }
        };

        run();

        return () => {
            cancelled = true;
            stopCamera();
        };
    }, [mode]);

    return (
        <>
            <div className="flex flex-col items-center gap-1.5">
                <p className="font-changa-one text-base text-center">
                    {t("signup.qr_restore_title")}
                </p>
                <p className="text-sm text-center opacity-60 leading-relaxed">
                    {t("signup.qr_restore_description")}
                </p>
            </div>

            <div className="flex gap-2 w-full">
                <ButtonHapticWrapper>
                    <button
                        type="button"
                        onClick={() => setMode("file")}
                        className={`flex-1 py-2 rounded-lg font-changa-one text-xs tracking-wide transition-[color,opacity] cursor-pointer flex items-center justify-center gap-1.5 ${
                            mode === "file" ? (isDark ? "bg-white/15 text-white" : "bg-white/30 text-current") : (isDark ? "bg-white/5 text-white/40" : "bg-white/10 opacity-50")
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t("signup.qr_restore_file")}
                    </button>
                </ButtonHapticWrapper>
                <ButtonHapticWrapper>
                    <button
                        type="button"
                        onClick={() => setMode("camera")}
                        className={`flex-1 py-2 rounded-lg font-changa-one text-xs tracking-wide transition-[color,opacity] cursor-pointer flex items-center justify-center gap-1.5 ${
                            mode === "camera" ? (isDark ? "bg-white/15 text-white" : "bg-white/30 text-current") : (isDark ? "bg-white/5 text-white/40" : "bg-white/10 opacity-50")
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {t("signup.qr_restore_camera")}
                    </button>
                </ButtonHapticWrapper>
            </div>

            {mode === "camera" && (
                <div id={SCANNER_ID} className="rounded-xl overflow-hidden bg-black/20 min-h-[200px] w-full" />
            )}

            {mode === "file" && (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files[0];

                        if(file) {
                            handleFileSelect(file);
                        }
                    }}
                    className={`flex flex-col items-center justify-center gap-3 py-14 rounded-xl border-2 border-dashed transition-colors cursor-pointer w-full ${
                        isDark
                            ? "border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10"
                            : "border-current/20 hover:border-current/40 bg-white/10 hover:bg-white/20"
                    }`}
                >
                    <svg className="w-10 h-10 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    <p className="text-sm font-changa-one opacity-50">{t("signup.qr_restore_choose")}</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];

                            if(file) {
                                handleFileSelect(file);
                            }
                        }}
                    />
                </div>
            )}

            <div id={SCANNER_ID} className="hidden" />

            {loading && (
                <span className="inline-block w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
            )}

            {error && (
                <p className="text-rose-400 text-xs text-center">{error}</p>
            )}

        </>
    );
};

export default QrRestoreStep;
